import React, { useEffect, useRef, useState } from "react";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import AdvancedForm from "./advanced_form/AdvancedForm";
import Form from "./form/Form";
import Results from "./results/Results";
import Documentation from "./Documentation";

function SimilaritySearchConsole() {
  const [activeAdvancedTab, setActiveAdvancedTab] = useState(null);
  const [activeQweryTab, setActiveQweryTab] = useState(null);
  const [activeRootTab, setActiveRootTab] = useState("qwery");
  const [fieldErrors, setFieldErrors] = useState({});
  const [generalError, setGeneralError] = useState(null);
  const [results, setResults] = useState(null);
  const [searchEndTime, setSearchEndTime] = useState(null);
  const searchStartTime = useRef(null);
  const [searchActive, setSearchActive] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);

  const startSearchTimer = () => {
    searchStartTime.current = new Date();
    setElapsedTime(0);
    setSearchActive(true);

    return setInterval(() => {
      if (searchStartTime.current) {
        setElapsedTime(Math.round((new Date() - searchStartTime.current) / 1000));
      }
    }, 1000);
  };

  const endSearchTimer = (intervalId) => {
    clearInterval(intervalId);
    // searchStartTime.current = null;
    setSearchActive(false);
    setSearchEndTime(new Date());
  };

  const handleSearch = async (searchParams) => {
    if (searchActive) return;

    setGeneralError(null);
    setFieldErrors({});
    setResults(null);

    const intervalId = startSearchTimer();

    const urlSearchParams = new URLSearchParams();
    const paramMapping = {
      searchModelTypes: "search_model_types",
      embeddingTypes: "embedding_types",
      withinDistance: "within_distance",
      orderBy: "order_by",
      modelTimestampWithinHours: "model_timestamp_within_hours",
      limit: "limit",
      searchVectorColumn: "search_vector_column",
      distanceType: "distance_type",
    };

    if (activeRootTab === "advanced" && activeAdvancedTab === "model") {
      paramMapping.queryModelId = "query_model_id";
      paramMapping.queryModelEmbeddingType = "query_model_embedding_type";
      paramMapping.queryModelType = "query_model_type";
    } else {
      paramMapping.query = "query";
    }

    Object.entries(searchParams).forEach(([key, value]) => {
      const mappedKey = paramMapping[key];
      if (mappedKey && value !== "") {
        urlSearchParams.append(mappedKey, value);
      }
    });

    urlSearchParams.append("root_tab", activeRootTab);
    urlSearchParams.append("qwery_tab", activeQweryTab);
    urlSearchParams.append("advanced_tab", activeAdvancedTab);
    window.history.pushState({}, "", `?${urlSearchParams.toString()}`);

    try {
      const response = await fetch(`/match_feed/api/similarity_search?${urlSearchParams.toString()}`, {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          ...authenticatedHeadersHash(),
        }),
      });

      endSearchTimer(intervalId);

      if (response.ok) {
        const json = await response.json();
        setResults(json.data.results);
      } else {
        const json = await response.json();
        if (json.errors) {
          const errors = json.errors[0];
          setGeneralError(errors.detail);
          if (errors.source) {
            setFieldErrors((prevErrors) => ({ ...prevErrors, [errors.source]: errors.detail }));
          }
        } else {
          setGeneralError(`Error: ${response.status} ${response.statusText}`);
        }
      }
    } catch (error) {
      endSearchTimer(intervalId);
      setGeneralError("An error occurred while performing the search.");
    }
  };

  // Overrides form defaults with params parsed from the URL, if present
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    let value = urlParams.get("root_tab");
    if (value) {
      setActiveRootTab(value);
    }
    value = urlParams.get("qwery_tab");
    if (value) {
      setActiveQweryTab(value);
    } else {
      setActiveQweryTab("resources_for_media");
    }
    value = urlParams.get("advanced_tab");
    if (value) {
      setActiveAdvancedTab(value);
    } else {
      setActiveAdvancedTab("query");
    }
  }, []);

  const clearResults = () => {
    searchStartTime.current = null;
    setFieldErrors({});
    setGeneralError(null);
    setResults(null);
    setSearchActive(false);
    setSearchEndTime(null);
  };

  const handleRootTabClick = (tab) => {
    setActiveRootTab(tab);
    clearResults();
  };

  const handleAdvancedTabClick = (tab) => {
    setActiveAdvancedTab(tab);
    clearResults();
  };

  const handleQweryTabClick = (tab) => {
    setActiveQweryTab(tab);
    clearResults();
  };

  const renderDuration = () => {
    return searchEndTime ? `${(searchEndTime - searchStartTime.current) / 1000} seconds` : "calculating...";
  };

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-md-8">
          {generalError && (
            <div className="alert alert-danger" role="alert">
              {generalError}
            </div>
          )}
          <Documentation />
          <ul className="nav nav-pills mb-3">
            <li className="nav-item">
              <button
                className={`nav-link ${activeRootTab === "qwery" ? "active" : ""}`}
                onClick={() => handleRootTabClick("qwery")}
                type="button"
              >
                Qwery
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeRootTab === "advanced" ? "active" : ""}`}
                onClick={() => handleRootTabClick("advanced")}
                type="button"
              >
                Advanced
              </button>
            </li>
          </ul>
          {activeRootTab === "qwery" && (
            <>
              <ul className="nav nav-tabs mb-3">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeQweryTab === "resources_for_media" ? "active" : ""}`}
                    onClick={() => handleQweryTabClick("resources_for_media")}
                    type="button"
                  >
                    Resources for media
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeQweryTab === "promotional_opportunities" ? "active" : ""}`}
                    onClick={() => handleQweryTabClick("promotional_opportunities")}
                    type="button"
                  >
                    Promotional opportunities
                  </button>
                </li>
              </ul>
              <Form
                activeTab={activeQweryTab}
                onSearch={handleSearch}
                fieldErrors={fieldErrors}
                searchActive={searchActive}
              />
            </>
          )}
          {activeRootTab === "advanced" && (
            <>
              <ul className="nav nav-tabs mb-3">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeAdvancedTab === "query" ? "active" : ""}`}
                    onClick={() => handleAdvancedTabClick("query")}
                    type="button"
                  >
                    Search by Text
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeAdvancedTab === "model" ? "active" : ""}`}
                    onClick={() => handleAdvancedTabClick("model")}
                    type="button"
                  >
                    Search by Object
                  </button>
                </li>
              </ul>
              <AdvancedForm
                activeTab={activeAdvancedTab}
                onSearch={handleSearch}
                fieldErrors={fieldErrors}
                searchActive={searchActive}
              />
            </>
          )}
          {searchActive && (
            <div>
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p>Elapsed Time: {elapsedTime} seconds</p>
            </div>
          )}
          {results && <Results results={results} searchDuration={renderDuration()} />}
        </div>
      </div>
    </div>
  );
}

export default SimilaritySearchConsole;
